import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { supabase } from '../../../lib/supabase';
import { useArtistStore } from '../../../hooks/useArtistStore';
import { FavoriteButton } from '../../../components/feature/FavoriteButton';
import { useArtistGenreColors, findGenreColor } from '../../../hooks/useGenreColors';

interface SearchArtist {
  id: string;
  name: string;
  genre: string;
  location: string;
  color: string;
  image: string;
  platforms: string[];
  isRegistered: boolean;
}

interface DbGenre {
  id: string;
  name: string;
  color: string;
  attribute_id: string;
}

interface Attribute {
  id: string;
  name: string;
  color: string;
}

const SNS_PLATFORMS = [
  { id: 'instagram', label: 'Instagram', icon: 'ri-instagram-line' },
  { id: 'twitter', label: 'X / Twitter', icon: 'ri-twitter-x-line' },
  { id: 'youtube', label: 'YouTube', icon: 'ri-youtube-line' },
  { id: 'soundcloud', label: 'SoundCloud', icon: 'ri-soundcloud-line' },
  { id: 'spotify', label: 'Spotify', icon: 'ri-spotify-line' },
  { id: 'tiktok', label: 'TikTok', icon: 'ri-tiktok-line' },
];

// 属性ごとのアイコンマッピング（名前に応じてアイコンを変える）
const ATTRIBUTE_ICON_MAP: Record<string, string> = {
  '奏者': 'ri-guitar-line',
  '制作者': 'ri-equalizer-line',
  '音響': 'ri-sound-module-line',
};

function getAttributeIcon(name: string): string {
  for (const key of Object.keys(ATTRIBUTE_ICON_MAP)) {
    if (name.includes(key)) return ATTRIBUTE_ICON_MAP[key];
  }
  return 'ri-music-2-line';
}

// ─── 並び替え定義 ──────────────────────────────────────────────────────────────

type ArtistSortOrder = 'default' | 'popular' | 'newest' | 'prefecture' | 'genre';

const artistSortOptions: Array<{ id: ArtistSortOrder; label: string; icon: string }> = [
  { id: 'default',    label: 'おすすめ順',     icon: 'ri-sparkling-line' },
  { id: 'popular',    label: '人気順',         icon: 'ri-fire-line' },
  { id: 'newest',     label: '新着順',         icon: 'ri-time-line' },
  { id: 'prefecture', label: '都道府県順',     icon: 'ri-map-pin-line' },
  { id: 'genre',      label: 'ジャンル順',     icon: 'ri-music-2-line' },
];

export function ArtistSearch() {
  const navigate = useNavigate();
  const { artists: storeArtists } = useArtistStore();
  const genreColorMap = useArtistGenreColors();
  const [query, setQuery] = useState('');
  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  const [dbArtists, setDbArtists] = useState<SearchArtist[]>([]);
  const [loading, setLoading] = useState(true);
  const [showGenreFilter, setShowGenreFilter] = useState(false);
  const [showPlatformFilter, setShowPlatformFilter] = useState(false);
  const [sortOrder, setSortOrder] = useState<ArtistSortOrder>('default');
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);
  const sortDropdownRef = useRef<HTMLDivElement>(null);

  const [dbGenres, setDbGenres] = useState<DbGenre[]>([]);
  const [attributes, setAttributes] = useState<Attribute[]>([]);

  // ドロップダウン外クリックで閉じる
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(e.target as Node)) {
        setSortDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ジャンル＆属性を取得
  useEffect(() => {
    const fetchGenresAndAttributes = async () => {
      const [attrRes, genreRes] = await Promise.all([
        supabase.from('artist_attributes').select('id, name, color').order('sort_order'),
        supabase.from('artist_main_genres').select('id, name, color, attribute_id').order('sort_order'),
      ]);
      if (attrRes.data) setAttributes(attrRes.data);
      if (genreRes.data) setDbGenres(genreRes.data);
    };
    fetchGenresAndAttributes();
  }, []);

  // Fetch DB artists
  useEffect(() => {
    const fetchArtists = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('artist_layers')
          .select('user_id, artist_name, genre, location, color, profile_image_url, socials, instagram, twitter, youtube, soundcloud, spotify, tiktok, prefecture');

        if (error) throw error;

        const userIds = (data || []).map((row) => row.user_id);
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, user_type')
          .in('id', userIds)
          .eq('user_type', 'maker');

        const makerIds = new Set((profiles || []).map((p) => p.id));

        const results: SearchArtist[] = (data || [])
          .filter((row) => makerIds.has(row.user_id))
          .map((row) => {
            const platforms: string[] = [];
            if (row.instagram) platforms.push('instagram');
            if (row.twitter) platforms.push('twitter');
            if (row.youtube) platforms.push('youtube');
            if (row.soundcloud) platforms.push('soundcloud');
            if (row.spotify) platforms.push('spotify');
            if (row.tiktok) platforms.push('tiktok');

            if (row.socials && Array.isArray(row.socials)) {
              (row.socials as { platform: string }[]).forEach((s) => {
                const name = (s.platform || '').toLowerCase();
                if (name.includes('instagram') && !platforms.includes('instagram')) platforms.push('instagram');
                else if ((name.includes('twitter') || name.includes('x')) && !platforms.includes('twitter')) platforms.push('twitter');
                else if (name.includes('youtube') && !platforms.includes('youtube')) platforms.push('youtube');
                else if (name.includes('soundcloud') && !platforms.includes('soundcloud')) platforms.push('soundcloud');
                else if (name.includes('spotify') && !platforms.includes('spotify')) platforms.push('spotify');
                else if (name.includes('tiktok') && !platforms.includes('tiktok')) platforms.push('tiktok');
              });
            }

            return {
              id: `db-${row.user_id}`,
              name: row.artist_name || 'UNKNOWN',
              genre: row.genre || '',
              location: row.location || row.prefecture || '',
              color: row.color || '#00ff41',
              image: row.profile_image_url || '',
              platforms,
              isRegistered: true,
            };
          });

        setDbArtists(results);
      } catch (err) {
        console.error('Failed to fetch artists:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchArtists();
  }, []);

  const allArtists = useMemo(() => {
    const mockMapped: SearchArtist[] = storeArtists.map((a) => ({
      id: `mock-${a.id}`,
      name: a.name,
      genre: a.genre,
      location: a.location,
      color: a.color,
      image: a.image,
      platforms: a.platforms,
      isRegistered: false,
    }));
    return [...dbArtists, ...mockMapped];
  }, [dbArtists, storeArtists]);

  const filteredArtists = useMemo(() => {
    let results = allArtists;

    if (query.trim()) {
      const q = query.toLowerCase().trim();
      results = results.filter(
        (a) =>
          a.name.toLowerCase().includes(q) ||
          a.genre.toLowerCase().includes(q) ||
          a.location.toLowerCase().includes(q)
      );
    }

    if (selectedGenres.length > 0) {
      results = results.filter((a) =>
        selectedGenres.some((g) => a.genre.toLowerCase().includes(g.toLowerCase()))
      );
    }

    if (selectedPlatforms.length > 0) {
      results = results.filter((a) =>
        selectedPlatforms.some((p) => a.platforms.includes(p))
      );
    }

    // 並び替え
    const sorted = [...results];
    switch (sortOrder) {
      case 'popular':
        sorted.sort((a, b) => {
          const aScore = (a.isRegistered ? 10 : 0) + a.platforms.length;
          const bScore = (b.isRegistered ? 10 : 0) + b.platforms.length;
          return bScore - aScore;
        });
        break;
      case 'newest':
        sorted.sort((a, b) => {
          // 登録済みアーティストを優先、その後IDの降順
          if (a.isRegistered && !b.isRegistered) return -1;
          if (!a.isRegistered && b.isRegistered) return 1;
          return b.id.localeCompare(a.id);
        });
        break;
      case 'prefecture':
        sorted.sort((a, b) => {
          const locA = a.location || 'zzz';
          const locB = b.location || 'zzz';
          return locA.localeCompare(locB, 'ja');
        });
        break;
      case 'genre':
        sorted.sort((a, b) => {
          const genreA = a.genre || 'zzz';
          const genreB = b.genre || 'zzz';
          return genreA.localeCompare(genreB, 'ja');
        });
        break;
      default:
        // デフォルト: 登録済み優先
        sorted.sort((a, b) => {
          if (a.isRegistered && !b.isRegistered) return -1;
          if (!a.isRegistered && b.isRegistered) return 1;
          return 0;
        });
        break;
    }

    return sorted;
  }, [allArtists, query, selectedGenres, selectedPlatforms, sortOrder]);

  const toggleGenre = useCallback((genre: string) => {
    setSelectedGenres((prev) =>
      prev.includes(genre) ? prev.filter((g) => g !== genre) : [...prev, genre]
    );
  }, []);

  const togglePlatform = useCallback((platform: string) => {
    setSelectedPlatforms((prev) =>
      prev.includes(platform) ? prev.filter((p) => p !== platform) : [...prev, platform]
    );
  }, []);

  const clearAll = useCallback(() => {
    setQuery('');
    setSelectedGenres([]);
    setSelectedPlatforms([]);
  }, []);

  const hasFilters = query.trim() || selectedGenres.length > 0 || selectedPlatforms.length > 0;

  const handleArtistClick = (artist: SearchArtist) => {
    if (artist.id.startsWith('db-')) {
      const userId = artist.id.replace('db-', '');
      navigate(`/artist/u/${userId}`);
    } else {
      const mockId = artist.id.replace('mock-', '');
      navigate(`/artist/${mockId}`);
    }
  };

  const platformIconMap: Record<string, string> = {
    youtube: 'ri-youtube-line',
    soundcloud: 'ri-soundcloud-line',
    spotify: 'ri-spotify-line',
    instagram: 'ri-instagram-line',
    twitter: 'ri-twitter-x-line',
    tiktok: 'ri-tiktok-line',
  };

  // 属性ごとにジャンルをグループ化
  const genresByAttribute = useMemo(() => {
    return attributes.map((attr) => ({
      attribute: attr,
      genres: dbGenres.filter((g) => g.attribute_id === attr.id),
    })).filter((group) => group.genres.length > 0);
  }, [attributes, dbGenres]);

  const currentSort = artistSortOptions.find(s => s.id === sortOrder) || artistSortOptions[0];

  return (
    <div>
      <div
        className="bg-[#0a0a0a] rounded-2xl border border-white/5 p-4 sm:p-6 lg:p-8"
        style={{ boxShadow: '0 0 40px rgba(255, 0, 110, 0.03)' }}
      >
        {/* Search Bar */}
        <div className="relative mb-4">
          <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
            <i className="ri-search-line text-gray-500 text-sm" />
          </div>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="アーティスト名・ジャンル・場所で検索..."
            className="w-full bg-white/5 border border-white/10 rounded-xl pl-11 pr-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-neon-pink/50 transition-colors duration-300"
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-gray-400 text-xs" />
            </button>
          )}
        </div>

        {/* Filter Toggle Buttons + Sort */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <button
            onClick={() => setShowGenreFilter(!showGenreFilter)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs tracking-wider font-bold transition-all duration-300 cursor-pointer border whitespace-nowrap ${
              showGenreFilter || selectedGenres.length > 0
                ? 'bg-neon-pink/10 border-neon-pink/30 text-neon-pink'
                : 'bg-white/5 border-white/10 text-gray-400 hover:border-white/20'
            }`}
          >
            <i className="ri-music-2-line text-sm" />
            GENRE
            {selectedGenres.length > 0 && (
              <span className="ml-1 px-1.5 py-0.5 rounded-full bg-neon-pink/20 text-neon-pink text-[9px]">
                {selectedGenres.length}
              </span>
            )}
            <i className={`ri-arrow-${showGenreFilter ? 'up' : 'down'}-s-line text-sm`} />
          </button>

          <button
            onClick={() => setShowPlatformFilter(!showPlatformFilter)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs tracking-wider font-bold transition-all duration-300 cursor-pointer border whitespace-nowrap ${
              showPlatformFilter || selectedPlatforms.length > 0
                ? 'bg-neon-green/10 border-neon-green/30 text-neon-green'
                : 'bg-white/5 border-white/10 text-gray-400 hover:border-white/20'
            }`}
          >
            <i className="ri-share-line text-sm" />
            SNS
            {selectedPlatforms.length > 0 && (
              <span className="ml-1 px-1.5 py-0.5 rounded-full bg-neon-green/20 text-neon-green text-[9px]">
                {selectedPlatforms.length}
              </span>
            )}
            <i className={`ri-arrow-${showPlatformFilter ? 'up' : 'down'}-s-line text-sm`} />
          </button>

          {hasFilters && (
            <button
              onClick={clearAll}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs tracking-wider text-gray-500 hover:text-white bg-white/5 border border-white/10 hover:border-white/20 transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-filter-off-line text-sm" />
              クリア
            </button>
          )}

          {/* Spacer */}
          <div className="flex-1" />

          {/* Sort Dropdown */}
          <div className="relative flex-shrink-0" ref={sortDropdownRef}>
            <button
              onClick={() => setSortDropdownOpen(prev => !prev)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg border border-white/10 bg-white/5 hover:bg-white/8 hover:border-white/20 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className={`${currentSort.icon} text-neon-pink text-sm w-4 h-4 flex items-center justify-center`} />
              <span className="text-gray-300 text-xs font-bold tracking-wider hidden sm:inline">{currentSort.label}</span>
              <i className={`ri-arrow-down-s-line text-gray-500 text-sm w-4 h-4 flex items-center justify-center transition-transform duration-200 ${sortDropdownOpen ? 'rotate-180' : ''}`} />
            </button>

            {/* Dropdown Menu */}
            {sortDropdownOpen && (
              <motion.div
                initial={{ opacity: 0, y: -8, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -8, scale: 0.96 }}
                transition={{ duration: 0.15 }}
                className="absolute right-0 top-full mt-2 w-52 rounded-xl border border-white/10 overflow-hidden z-30"
                style={{ background: '#111111', boxShadow: '0 16px 48px rgba(0,0,0,0.6)' }}
              >
                <div className="px-3 py-2 border-b border-white/5">
                  <span className="text-gray-600 text-[10px] tracking-[0.2em] uppercase font-bold">並び替え</span>
                </div>
                {artistSortOptions.map(opt => {
                  const isSelected = sortOrder === opt.id;
                  return (
                    <button
                      key={opt.id}
                      onClick={() => { setSortOrder(opt.id); setSortDropdownOpen(false); }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-left transition-all cursor-pointer hover:bg-white/5"
                      style={isSelected ? { background: 'rgba(255,0,110,0.08)' } : undefined}
                    >
                      <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                        <i
                          className={`${opt.icon} text-sm`}
                          style={{ color: isSelected ? '#ff006e' : '#6b7280' }}
                        />
                      </div>
                      <span
                        className="text-xs font-medium tracking-wide"
                        style={{ color: isSelected ? '#ff006e' : '#9ca3af' }}
                      >
                        {opt.label}
                      </span>
                      {isSelected && (
                        <div className="ml-auto w-4 h-4 flex items-center justify-center">
                          <i className="ri-check-line text-sm" style={{ color: '#ff006e' }} />
                        </div>
                      )}
                    </button>
                  );
                })}
              </motion.div>
            )}
          </div>
        </div>

        {/* Genre Filter Panel — 属性ごとにグループ化 */}
        {showGenreFilter && (
          <div className="mb-4 rounded-xl border border-white/8 overflow-hidden">
            {genresByAttribute.length === 0 ? (
              <div className="p-4">
                <p className="text-gray-600 text-xs tracking-wider">ジャンルが登録されていません</p>
              </div>
            ) : (
              genresByAttribute.map((group, idx) => {
                const icon = getAttributeIcon(group.attribute.name);
                const attrColor = group.attribute.color || '#ff006e';
                const selectedInGroup = group.genres.filter((g) => selectedGenres.includes(g.name));
                return (
                  <div
                    key={group.attribute.id}
                    className={idx !== 0 ? 'border-t border-white/5' : ''}
                  >
                    {/* グループヘッダー */}
                    <div
                      className="flex items-center justify-between px-4 py-3"
                      style={{ background: `${attrColor}08` }}
                    >
                      <div className="flex items-center gap-2">
                        <div
                          className="w-6 h-6 flex items-center justify-center rounded-md"
                          style={{ background: `${attrColor}18`, border: `1px solid ${attrColor}30` }}
                        >
                          <i className={`${icon} text-xs`} style={{ color: attrColor }} />
                        </div>
                        <span
                          className="text-[11px] font-bold tracking-[0.2em] uppercase"
                          style={{ color: attrColor }}
                        >
                          {group.attribute.name}
                        </span>
                        <span
                          className="text-[9px] tracking-wider px-1.5 py-0.5 rounded-full"
                          style={{
                            background: `${attrColor}12`,
                            color: `${attrColor}80`,
                            border: `1px solid ${attrColor}20`,
                          }}
                        >
                          {group.genres.length}
                        </span>
                      </div>
                      {selectedInGroup.length > 0 && (
                        <span
                          className="text-[9px] font-bold tracking-wider px-2 py-0.5 rounded-full"
                          style={{
                            background: `${attrColor}20`,
                            color: attrColor,
                            border: `1px solid ${attrColor}40`,
                          }}
                        >
                          {selectedInGroup.length} 選択中
                        </span>
                      )}
                    </div>

                    {/* ジャンルタグ */}
                    <div className="px-4 py-3 flex flex-wrap gap-2 bg-black/20">
                      {group.genres.map((genre) => {
                        const isActive = selectedGenres.includes(genre.name);
                        return (
                          <button
                            key={genre.id}
                            onClick={() => toggleGenre(genre.name)}
                            className="px-3 py-1.5 rounded-full text-[10px] tracking-wider font-bold transition-all duration-300 cursor-pointer border whitespace-nowrap"
                            style={{
                              background: isActive ? `${genre.color}22` : 'transparent',
                              borderColor: isActive ? `${genre.color}55` : 'rgba(255,255,255,0.08)',
                              color: isActive ? genre.color : 'rgba(156,163,175,0.8)',
                              boxShadow: isActive ? `0 0 14px ${genre.color}30` : 'none',
                            }}
                          >
                            {isActive && (
                              <i className="ri-check-line mr-1 text-[9px]" />
                            )}
                            {genre.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* SNS Platform Filter Panel */}
        {showPlatformFilter && (
          <div className="mb-4 p-4 rounded-xl bg-white/[0.02] border border-white/5">
            <div className="flex items-center gap-2 mb-3">
              <i className="ri-share-line text-neon-green text-xs" />
              <span className="text-gray-400 text-[10px] tracking-[0.2em] uppercase">SNS Platform Filter</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {SNS_PLATFORMS.map((platform) => {
                const isActive = selectedPlatforms.includes(platform.id);
                return (
                  <button
                    key={platform.id}
                    onClick={() => togglePlatform(platform.id)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-[10px] tracking-wider font-bold transition-all duration-300 cursor-pointer border whitespace-nowrap ${
                      isActive
                        ? 'bg-neon-green/15 border-neon-green/40 text-neon-green'
                        : 'bg-transparent border-white/10 text-gray-500 hover:border-white/20 hover:text-gray-300'
                    }`}
                    style={isActive ? { boxShadow: '0 0 12px rgba(0, 255, 65, 0.2)' } : undefined}
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className={`${platform.icon} text-sm`} />
                    </div>
                    {platform.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Active Filters Summary */}
        {hasFilters && (
          <div className="flex flex-wrap items-center gap-2 mb-4 px-1">
            <span className="text-gray-600 text-[10px] tracking-wider">FILTERS:</span>
            {selectedGenres.map((g) => {
              const matched = dbGenres.find((dg) => dg.name === g);
              const color = matched?.color || '#ff006e';
              return (
                <span
                  key={g}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] tracking-wider cursor-pointer transition-colors"
                  style={{
                    background: `${color}15`,
                    border: `1px solid ${color}30`,
                    color,
                  }}
                  onClick={() => toggleGenre(g)}
                >
                  {g}
                  <i className="ri-close-line text-[9px]" />
                </span>
              );
            })}
            {selectedPlatforms.map((p) => {
              const plat = SNS_PLATFORMS.find((s) => s.id === p);
              return (
                <span
                  key={p}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-neon-green/10 border border-neon-green/20 text-neon-green text-[9px] tracking-wider cursor-pointer hover:bg-neon-green/20 transition-colors"
                  onClick={() => togglePlatform(p)}
                >
                  <i className={`${plat?.icon || 'ri-link'} text-[9px]`} />
                  {plat?.label || p}
                  <i className="ri-close-line text-[9px]" />
                </span>
              );
            })}
            {query.trim() && (
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/10 border border-white/20 text-white text-[9px] tracking-wider cursor-pointer hover:bg-white/15 transition-colors"
                onClick={() => setQuery('')}
              >
                &quot;{query}&quot;
                <i className="ri-close-line text-[9px]" />
              </span>
            )}
          </div>
        )}

        {/* Results Count */}
        <div className="flex items-center justify-between mb-4 px-1">
          <span className="text-gray-500 text-xs">
            {hasFilters ? (
              <>
                <span className="text-white font-bold">{filteredArtists.length}</span>
                <span className="mx-1">/</span>
                {allArtists.length} 件のアーティスト
              </>
            ) : (
              <>全 <span className="text-white font-bold">{allArtists.length}</span> 件のアーティスト</>
            )}
          </span>
          <div className="flex items-center gap-2">
            {sortOrder !== 'default' && (
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold tracking-wider"
                style={{ background: 'rgba(255,0,110,0.12)', color: '#ff006e', border: '1px solid rgba(255,0,110,0.3)' }}
              >
                <i className={`${currentSort.icon} text-[9px]`} />
                {currentSort.label}
              </span>
            )}
            {hasFilters && filteredArtists.length === 0 && (
              <span className="text-gray-600 text-[10px] tracking-wider">NO RESULTS</span>
            )}
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-16">
            <i className="ri-loader-4-line text-2xl text-neon-pink animate-spin" />
          </div>
        )}

        {/* Results Grid */}
        {!loading && filteredArtists.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 lg:gap-4">
            {filteredArtists.map((artist) => {
              const genreColor = findGenreColor(artist.genre, genreColorMap, artist.color);
              return (
                <div
                  key={artist.id}
                  className="group cursor-pointer"
                  onClick={() => handleArtistClick(artist)}
                >
                  <div
                    className="relative bg-[#111] rounded-xl overflow-hidden transition-all duration-500 hover:-translate-y-1"
                    style={{ border: `1px solid ${artist.color}25` }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLElement).style.boxShadow = `0 8px 30px ${artist.color}30`;
                      (e.currentTarget as HTMLElement).style.borderColor = `${artist.color}50`;
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLElement).style.boxShadow = 'none';
                      (e.currentTarget as HTMLElement).style.borderColor = `${artist.color}25`;
                    }}
                  >
                    {/* Image */}
                    <div className="relative w-full h-40 sm:h-48 overflow-hidden">
                      {artist.image ? (
                        <img
                          src={artist.image}
                          alt={artist.name}
                          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
                        />
                      ) : (
                        <div
                          className="w-full h-full flex items-center justify-center"
                          style={{ background: `linear-gradient(135deg, ${artist.color}20, ${artist.color}05)` }}
                        >
                          <i className="ri-user-line text-4xl" style={{ color: `${artist.color}60` }} />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-[#111] via-transparent to-transparent" />

                      {artist.isRegistered && (
                        <div className="absolute top-2 left-2 px-1.5 py-0.5 rounded-full bg-neon-green/20 border border-neon-green/40">
                          <span className="text-neon-green text-[7px] font-bold tracking-wider">REGISTERED</span>
                        </div>
                      )}

                      <div className="absolute top-2 right-2 z-10">
                        <FavoriteButton
                          artistId={artist.id.startsWith('db-') ? artist.id.replace('db-', '') : artist.id.replace('mock-', '')}
                          artistType={artist.isRegistered ? 'artist' : 'mock'}
                          size="sm"
                        />
                      </div>

                      <div className="absolute bottom-2 right-2 flex gap-1">
                        {artist.platforms.slice(0, 3).map((platform) => (
                          <div
                            key={platform}
                            className="w-5 h-5 flex items-center justify-center rounded-full bg-black/60 backdrop-blur-sm"
                          >
                            <i className={`${platformIconMap[platform] || 'ri-music-line'} text-[9px] text-white/70`} />
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Info */}
                    <div className="px-3 pt-2 pb-3">
                      <span className="text-[8px] tracking-widest block mb-0.5" style={{ color: artist.color }}>
                        {artist.location || 'JAPAN'}
                      </span>
                      <h4 className="text-white text-xs sm:text-sm font-bold tracking-wide truncate mb-1">
                        {artist.name}
                      </h4>
                      <div className="w-6 h-0.5 mb-1.5" style={{ background: artist.color, boxShadow: `0 0 6px ${artist.color}` }} />
                      <span
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[8px] sm:text-[9px] font-bold tracking-wider"
                        style={{
                          background: `${genreColor}15`,
                          color: genreColor,
                          border: `1px solid ${genreColor}30`,
                        }}
                      >
                        <span
                          className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                          style={{ background: genreColor, boxShadow: `0 0 4px ${genreColor}` }}
                        />
                        <span className="truncate max-w-[100px]">{artist.genre || 'Artist'}</span>
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Empty State */}
        {!loading && hasFilters && filteredArtists.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-white/5 mb-4">
              <i className="ri-search-line text-2xl text-gray-600" />
            </div>
            <p className="text-gray-500 text-sm mb-1">該当するアーティストが見つかりません</p>
            <p className="text-gray-600 text-xs mb-4">検索条件を変更してみてください</p>
            <button
              onClick={clearAll}
              className="px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-gray-400 text-xs tracking-wider hover:bg-white/10 hover:text-white transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              フィルターをクリア
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
